import {
  Attribute,
  BaseStyleId,
  MapLayer,
  MapSchema,
} from "@cartographerio/atlas-map";
import { Feature } from "@cartographerio/geometry";
import { chakra, Checkbox, Radio, Stack } from "@chakra-ui/react";
import lodash from "lodash";
import React, { MouseEvent, ReactElement, useCallback } from "react";
import { FaChevronRight } from "react-icons/fa";
import { Dict } from "../../hooks/useDict";
import FeaturesSelectedCount from "./FeaturesSelectedCount";
import InspectorBox from "./InspectorBox";
import InspectorButton from "./InspectorButton";
import InspectorHeading from "./InspectorHeading";
import InspectorItem from "./InspectorItem";
import InspectorTitle from "./InspectorTitle";

type OnLayerClick = (layer: MapLayer, focus: boolean, altKey: boolean) => void;

export interface RootInspectorProps {
  schema: MapSchema;
  layerVisibility: Dict<boolean>;
  currentAttributes: Dict<Attribute>;
  baseStyleId: BaseStyleId;
  setBaseStyleId: (style: BaseStyleId) => void;
  selectedFeatures: Dict<Feature[]>;
  onLayerClick: OnLayerClick;
  onClearFeatureSelection: () => void;
}

const baseStyleOptions: { id: BaseStyleId; text: string }[] = [
  { id: "terrain", text: "Terrain" },
  { id: "satellite", text: "Satellite" },
];

export default function RootInspector(props: RootInspectorProps): ReactElement {
  const {
    schema,
    layerVisibility,
    currentAttributes,
    baseStyleId,
    setBaseStyleId,
    selectedFeatures,
    onLayerClick,
    onClearFeatureSelection,
  } = props;

  return (
    <InspectorBox>
      <InspectorTitle>{schema.title ?? "Map Layers"}</InspectorTitle>

      <FeaturesSelectedCount
        count={Object.values(selectedFeatures).flat().length}
        onClear={onClearFeatureSelection}
      />

      <InspectorHeading>Data Layers</InspectorHeading>

      <Stack spacing="4">
        {schema.layers.map(layer => (
          <LayerItem
            key={layer.layerId}
            layer={layer}
            layerVisibility={layerVisibility}
            currentAttributes={currentAttributes}
            selectedFeatures={selectedFeatures}
            onLayerClick={onLayerClick}
          />
        ))}
      </Stack>

      <InspectorHeading>Base Layers</InspectorHeading>

      <Stack spacing="2" w="100%" alignItems="stretch">
        {baseStyleOptions.map(option => (
          <InspectorItem key={option.id}>
            <Radio
              pl="1"
              isChecked={baseStyleId === option.id}
              onChange={() => setBaseStyleId(option.id)}
            >
              <chakra.span fontSize="sm">{option.text}</chakra.span>
            </Radio>
          </InspectorItem>
        ))}
      </Stack>
    </InspectorBox>
  );
}

interface LayerItemProps {
  layer: MapLayer;
  layerVisibility: Dict<boolean>;
  currentAttributes: Dict<Attribute>;
  selectedFeatures: Dict<Feature[]>;
  onLayerClick: OnLayerClick;
}

function LayerItem(props: LayerItemProps): ReactElement {
  const {
    layer,
    layerVisibility,
    currentAttributes,
    selectedFeatures,
    onLayerClick,
  } = props;

  const areFeaturesSelected = !lodash.isEmpty(selectedFeatures);

  const visible = layerVisibility[layer.layerId] ?? true;

  const attribute = currentAttributes[layer.layerId] ?? undefined;

  const layerFeaturesSelected = selectedFeatures[layer.layerId]?.length;

  const subtitle = areFeaturesSelected
    ? layerFeaturesSelected
      ? `${layerFeaturesSelected} features selected`
      : null
    : attribute && attribute.label !== layer.title
    ? attribute.label
    : null;

  // We use the click event instead of the change event here because
  // we need to access `evt.altKey` (which isn't available on change events).
  const handleCheckboxClick = useCallback(
    (evt: MouseEvent<HTMLInputElement>) => {
      evt.preventDefault();
      evt.stopPropagation();
      onLayerClick(layer, false, evt.altKey);
    },
    [layer, onLayerClick]
  );

  const handleIconClick = useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      onLayerClick(layer, true, false);
    },
    [layer, onLayerClick]
  );

  const button = (
    <InspectorButton
      as={FaChevronRight}
      ariaLabel="View layer"
      onClick={handleIconClick}
    />
  );

  return (
    <InspectorItem right={button}>
      <Checkbox pl="1" isChecked={visible} onClick={handleCheckboxClick}>
        <chakra.div maxWidth="100%" fontSize="sm">
          {layer.title}
        </chakra.div>
        <chakra.div maxWidth="100%" fontSize="sm" color="gray.500">
          {subtitle}
        </chakra.div>
      </Checkbox>
    </InspectorItem>
  );
}
